import React, { useState, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { VehiclesService } from "@services";
import utils from "@utils";
import { Modal, ConfirmationModal } from "../../../../components";
import EditVehicleModal from "../EditVehicleModal/EditVehicleModal";
import "./ManageVehiclesModal.scss";

const SORT_BY = {
	NUMBER: "number",
	LAST_USED: "lastUsed",
	CURRENT_MILES: "currentMiles",
};

const ManageVehiclesModal = props => {
	const { lang } = global;
	const { show, onHide, user } = props;

	const [showEditVehicleModal, setShowEditVehicleModal] = useState(false);
	const [vehicles, setVehicles] = useState([]);
	const [selectedVehicle, setSelectedVehicle] = useState(null);
	const [sortBy, setSortBy] = useState(SORT_BY.NUMBER);
	const [sortAsc, setSortAsc] = useState(true);
	const [searchText, setSearchText] = useState("");
	const _confirmationModal = useRef(null);

	const sortedVehicles = useMemo(
		() =>
			vehicles.sort((a, b) => {
				if (sortBy === SORT_BY.NUMBER) {
					if (sortAsc) {
						return a.Number > b.Number ? 1 : -1;
					}
					return b.Number > a.Number ? 1 : -1;
				}
				if (sortBy === SORT_BY.LAST_USED) {
					if (sortAsc) {
						return new Date(a.LastDateUsed) > new Date(b.LastDateUsed) ? 1 : -1;
					}
					return new Date(b.LastDateUsed) > new Date(a.LastDateUsed) ? 1 : -1;
				}
				if (sortBy === SORT_BY.CURRENT_MILES) {
					if (sortAsc) {
						return a.RecentMiles > b.RecentMiles ? 1 : -1;
					}
					return b.RecentMiles > a.RecentMiles ? 1 : -1;
				}
				return 0;
			}),
		[vehicles, sortBy, sortAsc],
	);

	const filteredVehicles = useMemo(() => {
		if (searchText && searchText.trim().length > 0) {
			return sortedVehicles.filter(vehicle =>
				vehicle.Number.toLowerCase().includes(searchText.toLowerCase()),
			);
		}
		return sortedVehicles;
	}, [sortedVehicles, searchText, sortBy, sortAsc]);

	const getAllVehicles = () => {
		VehiclesService.getAll()
			.then(response => {
				setVehicles(response);
			})
			.catch(error =>
				utils.notify({
					type: "error",
					message: error.message ?? lang.t("Error loading vehicles"),
					timeOut: 1500,
				}),
			);
	};

	const deleteVehicle = vehicle => {
		VehiclesService.delete(vehicle.Id)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Vehicle deleted successfully"),
					timeOut: 1500,
				});
				getAllVehicles();
			})
			.catch(({ response }) =>
				utils.notify({
					type: "error",
					message: response?.data?.Message ?? lang.t("Error deleting vehicle"),
					timeOut: 1500,
				}),
			);
	};

	useEffect(() => {
		if (show) {
			getAllVehicles();
		}
	}, [show]);

	const addVehicle = () => {
		setSelectedVehicle(null);
		setShowEditVehicleModal(true);
	};

	const editVehicle = vehicle => {
		setSelectedVehicle(vehicle);
		setShowEditVehicleModal(true);
	};

	const openDeleteVehicleModal = vehicle => {
		_confirmationModal.current.openModal({
			title: lang.t("Delete Vehicle"),
			message: [
				<div
					className="text-center"
					key="reset-pwd-confirm"
				>
					{lang.t(
						"Are you sure you want to delete this vehicle number_ Past jobs will retain this vehicle number",
					)}
				</div>,
			],
			confirmButton: `${lang.t("Yes, Delete")}`,
			onConfirm: () => deleteVehicle(vehicle),
		});
	};

	const sortVehiclesBy = field => {
		if (field === sortBy) {
			setSortAsc(!sortAsc);
		} else {
			setSortBy(field);
		}
	};

	const searchVehicle = event => {
		const { value } = event.target;
		setSearchText(value || "");
	};

	return (
		<>
			<Modal
				id="manage-vehicles-modal"
				dialogClassName="manage-vehicles-dialog"
				size="md"
				show={show}
				onHide={() => onHide()}
			>
				<Modal.Header
					closeButton
					className="hidden-print"
				>
					<Modal.Title>{lang.t("Manage Vehicles")}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="container-fluid">
					<div className="actions-container">
						<input
							className="form-control"
							placeholder="Search..."
							value={searchText}
							onChange={searchVehicle}
						/>
						<button
							type="button"
							id="add-new-vehicle"
							className="btn btn-link"
							onClick={() => addVehicle()}
						>
							{`+ ${lang.t("Add Vehicle")}`}
						</button>
					</div>
					<div className="body-container">
						<div className="row vehicle-row">
							<button
								className="col-xs-3 tab-button"
								type="button"
								onClick={() => sortVehiclesBy(SORT_BY.NUMBER)}
							>
								<strong>{`${lang.t("Vehicle _")}`}</strong>
								<If condition={sortBy === SORT_BY.NUMBER}>
									<If condition={sortAsc}>
										<i className="glyphicon glyphicon-triangle-bottom" />
									</If>
									<If condition={!sortAsc}>
										<i className="glyphicon glyphicon-triangle-top" />
									</If>
								</If>
							</button>
							<button
								className="col-xs-4 tab-button"
								type="button"
								onClick={() => sortVehiclesBy(SORT_BY.LAST_USED)}
							>
								<strong>{`${lang.t("Date Last Used")}`}</strong>
								<If condition={sortBy === SORT_BY.LAST_USED}>
									<If condition={sortAsc}>
										<i className="glyphicon glyphicon-triangle-bottom" />
									</If>
									<If condition={!sortAsc}>
										<i className="glyphicon glyphicon-triangle-top" />
									</If>
								</If>
							</button>
							<button
								className="col-xs-3 tab-button"
								type="button"
								onClick={() => sortVehiclesBy(SORT_BY.CURRENT_MILES)}
							>
								<strong>{`${lang.t("Current Miles")}`}</strong>
								<If condition={sortBy === SORT_BY.CURRENT_MILES}>
									<If condition={sortAsc}>
										<i className="glyphicon glyphicon-triangle-bottom" />
									</If>
									<If condition={!sortAsc}>
										<i className="glyphicon glyphicon-triangle-top" />
									</If>
								</If>
							</button>
							<div className="col-xs-2" />
						</div>
						{filteredVehicles.map((vehicle, index) => (
							<div
								className="row vehicle-row"
								key={vehicle.Id}
								style={
									index % 2 === 0
										? { background: "#f0f0f0" }
										: { background: "white" }
								}
							>
								<div className="col-xs-3">
									<span>{vehicle.Number}</span>
								</div>
								<div className="col-xs-4">
									<span>
										{utils.formatDate(
											new Date(`${vehicle.LastDateUsed}Z`),
											"en-US",
											user.TimeZoneId,
											user.UserPreferences === "useMilitaryTime"
												? "dt-num-mil"
												: "dt-num",
										)}
									</span>
								</div>
								<div className="col-xs-3">
									<span>{vehicle.RecentMiles}</span>
								</div>
								<div className="col-xs-2 actions-column">
									<button
										type="button"
										className="btn btn-link"
										onClick={() => editVehicle(vehicle)}
									>
										<i className="rkf rkf-pencil" />
									</button>
									<button
										type="button"
										className="btn btn-link"
										onClick={() => openDeleteVehicleModal(vehicle)}
									>
										<i className="rkf rkf-trash text-danger" />
									</button>
								</div>
							</div>
						))}
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button
						id="button-go-back"
						className="btn btn-link hidden-print"
						type="button"
						onClick={() => onHide()}
					>
						{lang.t("Close")}
					</button>
				</Modal.Footer>
			</Modal>
			<ConfirmationModal ref={_confirmationModal} />
			<EditVehicleModal
				vehicle={selectedVehicle}
				show={showEditVehicleModal}
				onHide={() => setShowEditVehicleModal(false)}
				onSuccess={() => getAllVehicles()}
			/>
		</>
	);
};

ManageVehiclesModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func,
	user: PropTypes.object.isRequired,
};

ManageVehiclesModal.defaultProps = {
	show: false,
	onHide: () => {},
};

export default ManageVehiclesModal;
